import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getAssignableServiceAreaSuburbs } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

type AssignableSearchParams = Parameters<typeof getAssignableServiceAreaSuburbs>[0];

export const useAssignableServiceAreaSuburbs = (technicianId?: string) => {
  const [settledSearchTechnicianId, setSettledSearchTechnicianId] = useState<string>();
  // Compared by identity, so only the most recent search can update state.
  const latestSearchParamsRef = useRef<AssignableSearchParams | undefined>(undefined);

  const {
    mutate: searchAssignableSuburbsForRadius,
    data: assignableSuburbs,
    isLoading: isAssignableSuburbsLoading,
  } = useMutation(getAssignableServiceAreaSuburbs, {
    onError: (error: JsonDataItem, searchParams) => {
      if (searchParams !== latestSearchParamsRef.current) {
        return;
      }

      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
    onSettled: (_data, _error, searchParams) => {
      if (searchParams === latestSearchParamsRef.current) {
        setSettledSearchTechnicianId(searchParams.technicianId);
      }
    },
  });

  const searchAssignableSuburbs = useCallback(
    (radius: number) => {
      if (!technicianId) {
        return;
      }

      const searchParams = { technicianId, radius };
      latestSearchParamsRef.current = searchParams;
      searchAssignableSuburbsForRadius(searchParams);
    },
    [technicianId, searchAssignableSuburbsForRadius],
  );

  return {
    assignableSuburbs,
    isAssignableSuburbsLoading,
    searchAssignableSuburbs,
    settledSearchTechnicianId,
  };
};
